import { cache } from '../../cache-impl/cache-registry.js';
import { ClientApi } from '../../../api/client-api.js';
import { customElement } from 'lit/decorators.js';
import { DataCacheGeneric } from '../../../../webmodule-common/cache/generic-data-cache.js';
import { DevelopmentError } from '../../../../webmodule-common/other/development-error.js';
import { emptyGuid } from '../../../../webmodule-common/other/api/guid.js';
import { FranchiseeProjectDetailView } from './franchisee-project-detail-view.js';
import { FranchiseeProjectDocumentView } from './franchisee-project-document-view.js';
import { FranchiseeProjectResourceView } from './franchisee-project-resource-view.js';
import { getApiFactory } from '../../../api/api-injector.js';

import { ProjectDataEntryView, ProjectView, ProjectViewOptions } from '../../../projects/views/project-view.js';
import { ProjectDocumentView } from '../../../projects/views/project-document-view.js';
import { ProjectResourceView } from '../../../projects/views/project-resource-view.js';
import { ProjectState, Resource, ResourceType } from '../../../api/dealer-api-interface-project.js';
import { PurchaseOrderState } from '../../../api/dealer-api-interface-franchisee.js';
import { QuoteState } from '../../../api/dealer-api-interface-quote.js';
import { showValidations } from '../../../../webmodule-common/other/ui/modal-validationhandler.js';
import { LangFranchiseeProjectView } from '../../../../localization/scripts/dealer-franchisee/projects/views/franchisee-project-view-lang';

@customElement('wm-franchiseeprojectdeview')
export class FranchiseeProjectDEView extends ProjectDataEntryView {
  clientApi: ClientApi = getApiFactory().client();

  protected projectDetailViewFactory(_options: ProjectViewOptions) {
    return new FranchiseeProjectDetailView({
      projectManager: this.projectContainerManager
    });
  }

  protected projectResourceViewFactory(): ProjectResourceView {
    return new FranchiseeProjectResourceView({
      projectManager: this.projectContainerManager,
      quoteCache: cache().quote,
      purchaseOrderCache: cache().purchaseOrder,
      canClose: async () => {
        if (await this.canClose()) {
          await this.tryClose();
          return true;
        }
        return false;
      }
    });
  }

  protected projectDocumentsViewFactory(): ProjectDocumentView {
    return new FranchiseeProjectDocumentView({
      projectManager: this.projectContainerManager,
      canClose: async () => {
        if (await this.canClose()) {
          await this.tryClose();
          return true;
        }
        return false;
      }
    });
  }

  protected async internalCreateQuote(): Promise<boolean> {
    return await this.projectContainerManager.createProjectQuote();
  }

  protected async canCreateQuote(): Promise<boolean> {
    const project = this.projectContainer.project;
    if (!project) {
      throw new DevelopmentError(LangFranchiseeProjectView.projectContainerEmpty());
    }
    const isProjectActive = project.state !== ProjectState.Cancelled && project.state !== ProjectState.Completed;
    if (!isProjectActive) return false;
    const errors = this.getValidationErrors();

    if (errors.length !== 0) {
      await showValidations(errors);
      return false;
    }

    return true;
  }

  protected async setProjectState(projectState: ProjectState): Promise<boolean> {
    // force a reload of the project to get updated quote/purchase order resources
    this.projectContainer.project = null;
    await this.projectContainerManager.needsProject();

    const errors: string[] = [];
    const stateText = projectState === ProjectState.Cancelled ? 'cancel' : 'complete';
    const quoteResources = (await this.internalLoadResources(ResourceType.Quote, this.quoteCache)) ?? [];
    const orderResources =
      (await this.internalLoadResources(ResourceType.PurchaseOrder, this.purchaseOrderCache)) ?? [];

    if (projectState === ProjectState.Cancelled) {
      errors.push(...this.internalValidateProjectCancel(quoteResources, orderResources));
    } else if (projectState === ProjectState.Completed) {
      errors.push(...this.internalValidateProjectComplete(quoteResources, orderResources));
    }

    if (errors.length > 0) {
      await showValidations(errors, () => LangFranchiseeProjectView.unableToChangeProjectState({ stateText }));
      return false;
    } else {
      return super.setProjectState(projectState);
    }
  }

  protected async internalLoadResources(
    resourceType: ResourceType,
    objectCache: DataCacheGeneric
  ): Promise<Resource[] | undefined> {
    const items = this.projectContainer.resources?.filter(x => x.typeOf === resourceType);
    if (items) {
      const keys = items.map(x => x.resourceId ?? emptyGuid) ?? [];

      //if there are any other callbacks we need to do, add them to the
      //all array which will let them run at the same time
      await Promise.all([objectCache.preFetch(keys)]);
    }
    return items;
  }

  protected internalValidateProjectCancel(quoteResources: Resource[], orderResources: Resource[]): string[] {
    const errors: string[] = [];

    /*
         Cancel Requirements
         - Only Accepted, Rejected, Cancelled and Lapsed quotes
         - Only Cancelled purchase orders
         */
    let hasAccepted = false;
    quoteResources.forEach(resource => {
      const quoteSummary = this.quoteCache.getLocalData(resource.resourceId)?.quoteSummary;
      if (quoteSummary) {
        if (this.isQuoteStateOutstanding(quoteSummary.state)) {
          errors.push(
            LangFranchiseeProjectView.quoteOutstanding({
              quoteTitle: quoteSummary.title,
              quoteState: QuoteState[quoteSummary.state]
            })
          );
        } else if (quoteSummary.state === QuoteState.Approved) {
          //TODO# we may test for more than the accepted state with new supplier workflow
          hasAccepted = true;
        }
      }
    });
    // probably should never reach here, but just in case...
    if (hasAccepted) {
      errors.push(LangFranchiseeProjectView.cannotCancelProjectWithAcceptedQuotes());
    }

    orderResources.forEach(resource => {
      const orderSummary = this.purchaseOrderCache.getLocalData(resource.resourceId)?.purchaseOrder;
      if (
        orderSummary &&
        orderSummary.state !== PurchaseOrderState.Completed &&
        orderSummary.state !== PurchaseOrderState.Cancelled
      ) {
        errors.push(
          LangFranchiseeProjectView.purchaseOrderNotCompleted({
            purchaseOrderTitle: orderSummary.title,
            purchaseOrderState: PurchaseOrderState[orderSummary.state]
          })
        );
      }
    });

    return errors;
  }

  protected internalValidateProjectComplete(quoteResources: Resource[], orderResources: Resource[]): string[] {
    const errors: string[] = [];
    /*
         Complete Requirements
         - Only Accepted, Rejected, Cancelled and Lapsed quotes
         - Only Completed and Cancelled purchase orders
         */
    quoteResources.forEach(resource => {
      const quoteSummary = this.quoteCache.getLocalData(resource.resourceId)?.quoteSummary;
      if (quoteSummary && this.isQuoteStateOutstanding(quoteSummary.state)) {
        errors.push(
          LangFranchiseeProjectView.quoteNotCompleted({
            quoteTitle: quoteSummary.title,
            quoteState: QuoteState[quoteSummary.state]
          })
        );
      }
    });

    orderResources.forEach(resource => {
      const orderSummary = this.purchaseOrderCache.getLocalData(resource.resourceId)?.purchaseOrder;
      if (
        orderSummary &&
        orderSummary.state !== PurchaseOrderState.Completed &&
        orderSummary.state !== PurchaseOrderState.Cancelled
      ) {
        errors.push(
          LangFranchiseeProjectView.purchaseOrderNotCompleted({
            purchaseOrderTitle: orderSummary.title,
            purchaseOrderState: PurchaseOrderState[orderSummary.state]
          })
        );
      }
    });

    return errors;
  }

  protected isQuoteStateOutstanding(quoteState: QuoteState): boolean {
    return (
      quoteState !== QuoteState.Approved &&
      quoteState !== QuoteState.Cancelled &&
      quoteState !== QuoteState.Rejected &&
      quoteState !== QuoteState.Lapsed &&
      quoteState !== QuoteState.Accepted_RefusedBySupplier
    );
  }
}

@customElement('wm-franchiseeprojectview')
export class FranchiseeProjectView extends ProjectView {
  protected createView(): ProjectDataEntryView {
    return new FranchiseeProjectDEView(this.options, this);
  }
}
